import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type { CompensationResponse, StatementResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { useChartsShown } from '../../components/charts/useChartsShown';
import { PageHeader } from '../../components/PageHeader';
import { useAuthStore } from '../../auth/authStore';
import { money, readMoney } from '../catalog/money';
import { StatementsOverview } from '../insight/StatementsOverview';
import { clinicToday } from '../scheduling/clinicTime';
import { PanelHeading } from './PanelHeading';

/**
 * Doctor compensation and monthly statements (FR-906, DEC-041).
 *
 * The screen is built around one distinction: a month that has been <em>issued</em> is a document
 * somebody was paid against, and a month that has not is a number that can still move. Issued rows
 * show the date they were frozen; unissued ones show a button and, until the month has finished,
 * an explanation of why the button is refused rather than a button that fails.
 *
 * A doctor with no arrangement recorded shows "not set" rather than EGP 0.00. ASM-013 is still
 * open — which of the three models the partner clinics use is unconfirmed — so an empty statement
 * is the ordinary state of a new clinic, and a zero would look like an answer.
 *
 * Above the table, the month as charts (CR-35): delivered against owed, each doctor on four
 * measures, the month day by day, what was delivered, and six months of totals. They fold away.
 */
export function StatementsPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const canSetPay = useAuthStore((state) => state.permissions).has('DOCTOR_COMPENSATION_MANAGE');

  const [period, setPeriod] = useState(() => lastMonth());
  const [editing, setEditing] = useState<string | null>(null);
  const [chartsShown, showCharts] = useChartsShown('statements');

  const key = `${period.year}-${String(period.month).padStart(2, '0')}`;
  const statements = useApiQuery<StatementResponse[]>(
    queryKeys.statements(clinicId ?? '', key),
    `/clinics/${clinicId}/statements?year=${period.year}&month=${period.month}`,
    { enabled: Boolean(clinicId) },
  );

  const issue = useApiMutation<StatementResponse, { employmentId: string }>(
    ({ employmentId }) => ({
      path: `/clinics/${clinicId}/statements/${employmentId}/issue?year=${period.year}&month=${period.month}`,
      options: { method: 'POST', body: {} },
    }),
    { invalidates: [queryKeys.statements(clinicId ?? '', key)] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.statements')} description={t('home.noClinic')} />;
  }

  const monthHasFinished = hasFinished(period);

  return (
    <>
      <PageHeader
        title={t('nav.items.statements')}
        description={t('statements.description')}
        actions={
          <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
            {chartsShown ? t('overview.hide') : t('overview.show')}
          </button>
        }
      />

      <div className="filters">
        <label className="field">
          <span>{t('statements.period')}</span>
          <input
            type="month"
            value={key}
            onChange={(event) => {
              const [year, month] = event.target.value.split('-');
              if (year && month) {
                setPeriod({ year: Number(year), month: Number(month) });
              }
            }}
          />
        </label>
        {!monthHasFinished ? <p className="field__hint">{t('statements.monthInProgress')}</p> : null}
      </div>

      {chartsShown && (statements.data ?? []).length > 0 ? (
        <StatementsOverview
          clinicId={clinicId}
          period={period}
          statements={statements.data ?? []}
          today={clinicToday()}
        />
      ) : null}

      <AsyncSection
        query={statements}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('statements.emptyTitle')}
        emptyDescription={t('statements.emptyDescription')}
      >
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('statements.heading')} />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('statements.doctor')}</th>
                    <th scope="col">{t('statements.model')}</th>
                    <th scope="col" className="numeric">
                      {t('statements.sessions')}
                    </th>
                    <th scope="col" className="numeric">
                      {t('statements.delivered')}
                    </th>
                    <th scope="col" className="numeric">
                      {t('statements.total')}
                    </th>
                    <th scope="col">{t('statements.state')}</th>
                    <th scope="col">
                      <span className="visually-hidden">{t('common.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((statement) => (
                    <tr key={statement.employmentId}>
                      <th scope="row">{statement.doctorName}</th>
                      <td>
                        {t(`statements.models.${statement.model}`)}
                        {statement.model === 'PER_SESSION' && statement.rate
                          ? ` · ${money(statement.rate)}`
                          : null}
                        {statement.model === 'REVENUE_SHARE' && statement.sharePercent != null
                          ? ` · ${statement.sharePercent}%`
                          : null}
                      </td>
                      <td className="numeric">{statement.sessionsCompleted ?? 0}</td>
                      <td className="numeric">{money(statement.deliveredValue) ?? '—'}</td>
                      <td className="numeric">
                        {statement.model === 'UNSET' ? '—' : (money(statement.computedTotal) ?? '—')}
                      </td>
                      <td>
                        {statement.issuedAt ? (
                          <span className="badge badge--success">
                            {t('statements.issuedOn', {
                              at: new Date(statement.issuedAt).toLocaleDateString(),
                            })}
                          </span>
                        ) : (
                          <span className="badge badge--muted">{t('statements.draft')}</span>
                        )}
                      </td>
                      <td className="table__actions">
                        {canSetPay ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() =>
                              setEditing(editing === statement.employmentId ? null : (statement.employmentId ?? null))
                            }
                          >
                            {t('statements.setPay')}
                          </button>
                        ) : null}
                        {!statement.issuedAt && monthHasFinished && statement.model !== 'UNSET' ? (
                          <button
                            type="button"
                            className="button"
                            disabled={issue.isPending}
                            onClick={() => issue.mutate({ employmentId: statement.employmentId ?? '' })}
                          >
                            {t('statements.issue')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {editing ? (
              <CompensationEditor clinicId={clinicId} employmentId={editing} onDone={() => setEditing(null)} />
            ) : null}
          </section>
        )}
      </AsyncSection>
    </>
  );
}

/**
 * Recording a new arrangement (FR-906).
 *
 * Each model asks for exactly its own number and hides the others, because the API refuses a salary
 * with a percentage attached — and a form that let somebody type both would be a form that produces
 * a 422 after they had finished filling it in.
 */
function CompensationEditor({
  clinicId,
  employmentId,
  onDone,
}: {
  readonly clinicId: string;
  readonly employmentId: string;
  readonly onDone: () => void;
}) {
  const { t } = useTranslation();
  const [model, setModel] = useState<'SALARY' | 'PER_SESSION' | 'REVENUE_SHARE'>('PER_SESSION');

  const history = useApiQuery<CompensationResponse[]>(
    queryKeys.compensation(clinicId, employmentId),
    `/clinics/${clinicId}/statements/${employmentId}/compensation`,
  );

  const save = useApiMutation<CompensationResponse, FormData>(
    (form) => ({
      path: `/clinics/${clinicId}/statements/${employmentId}/compensation`,
      options: {
        method: 'PUT',
        body: {
          model,
          amount: model === 'REVENUE_SHARE' ? undefined : readMoney(form.get('amount')),
          sharePercent: model === 'REVENUE_SHARE' ? Number(form.get('share')) : undefined,
          effectiveFrom: String(form.get('effectiveFrom') ?? ''),
          note: String(form.get('note') ?? '') || undefined,
        },
      },
    }),
    {
      invalidates: [queryKeys.compensation(clinicId, employmentId)],
      onSuccess: onDone,
    },
  );

  return (
    <form
      className="panel"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(new FormData(event.currentTarget));
      }}
    >
      <PanelHeading title={t('statements.compensationHeading')} />
      <p className="panel__note">{t('statements.compensationNote')}</p>

      <div className="form-grid">
        <label className="field">
          <span>{t('statements.model')}</span>
          <select value={model} onChange={(event) => setModel(event.target.value as typeof model)}>
            <option value="SALARY">{t('statements.models.SALARY')}</option>
            <option value="PER_SESSION">{t('statements.models.PER_SESSION')}</option>
            <option value="REVENUE_SHARE">{t('statements.models.REVENUE_SHARE')}</option>
          </select>
        </label>

        {model === 'REVENUE_SHARE' ? (
          <label className="field">
            <span>{t('statements.share')}</span>
            <input name="share" type="number" min="0" max="100" step="0.01" required />
          </label>
        ) : (
          <label className="field">
            <span>{model === 'SALARY' ? t('statements.salary') : t('statements.fee')}</span>
            <input name="amount" type="number" min="0" step="0.01" required />
          </label>
        )}

        <label className="field">
          <span>{t('statements.effectiveFrom')}</span>
          <input name="effectiveFrom" type="date" required />
        </label>
        <label className="field">
          <span>{t('statements.note')}</span>
          <input name="note" type="text" maxLength={400} />
        </label>
      </div>

      <div className="table__actions">
        <button type="button" className="button button--ghost" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="button" disabled={save.isPending}>
          {t('common.save')}
        </button>
      </div>

      {(history.data ?? []).length > 0 ? (
        <ul className="stack">
          {(history.data ?? []).map((entry) => (
            <li key={entry.id}>
              {t(`statements.models.${entry.model}`)} ·{' '}
              {entry.model === 'REVENUE_SHARE' ? `${entry.sharePercent}%` : money(entry.amount)} ·{' '}
              {entry.effectiveFrom}
              {entry.effectiveTo ? ` → ${entry.effectiveTo}` : ` → ${t('statements.current')}`}
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}

function lastMonth(): { year: number; month: number } {
  const now = new Date();
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: previous.getFullYear(), month: previous.getMonth() + 1 };
}

/**
 * Whether the month is over.
 *
 * The API refuses to issue a month still running, so the button is hidden rather than shown and
 * then refused — the same rule stated twice, once where it is enforced and once where it would
 * otherwise waste somebody's click.
 */
function hasFinished({ year, month }: { year: number; month: number }): boolean {
  const now = new Date();
  return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
}
